import React, { useEffect, useRef, useState } from 'react';
import { getProvider, getProviderIds } from '../registry';
import type { IntegrationProviderConfig, ThirdPartyMode } from '../types';
import { RESERVED_DATA_KEYS } from '../constants';
import {
  enrichMergedWithObjectDefaults,
  serializeNodeToBlobConfig,
} from '../nodeConfigUtils';
import { useStoreThirdPartyIntegration } from '../../../store/storeThirdPartyIntegration';

export function useThirdPartyNodeState(data: Record<string, unknown>) {
  const [provider, setProviderState] = useState<string>(
    (data.provider as string) || getProviderIds()[0] || '',
  );
  const [mode, setMode] = useState<ThirdPartyMode>(
    (data.mode as ThirdPartyMode) || 'pull',
  );
  const [blobConfig, setBlobConfig] = useState<string>(
    (data.blobConfig as string) || '',
  );
  const [blobOpen, setBlobOpen] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, unknown>>(() => {
    const initial: Record<string, unknown> = { ...data };
    RESERVED_DATA_KEYS.forEach(k => delete initial[k]);
    if (
      data.output !== undefined &&
      initial.text !== undefined &&
      initial.text === data.output
    ) {
      initial.text = '';
    }
    return initial;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isChangingDirectly, setIsChangingDirectly] = useState(false);
  const [whoIsChanging, setWhoIsChanging] = useState<{
    name: string;
    value: unknown;
  }>({ name: '', value: '' });
  const [hasToExecute, setHasToExecute] = useState(true);
  const [validationErrorFieldName, setValidationErrorFieldName] = useState<
    string | null
  >(null);

  const userEditedFieldsRef = useRef<Set<string>>(new Set());
  const dataRef = useRef(data);
  const formValuesRef = useRef(formValues);
  dataRef.current = data;
  formValuesRef.current = formValues;

  const isProviderConnected = useStoreThirdPartyIntegration(state =>
    state.isProviderConnected(provider),
  );
  const config: IntegrationProviderConfig | undefined = provider
    ? getProvider(provider)
    : undefined;
  const isNodeConnected =
    !!(data.integrationId as string | undefined) ||
    isProviderConnected ||
    !!(data.connectedByUserId as string | undefined);

  useEffect(() => {
    const fromData: Record<string, unknown> = {};
    Object.keys(data).forEach(k => {
      if (RESERVED_DATA_KEYS.has(k) || k.endsWith('Data')) return;
      if (data[k] === undefined) return;
      if (
        k === 'text' &&
        data.output !== undefined &&
        data[k] === data.output
      ) {
        return;
      }
      if (userEditedFieldsRef.current.has(k)) return;
      const dataArray = data[k + 'Data'];
      const hasDataFromEdge = Array.isArray(dataArray) && dataArray.length >= 1;
      if (!hasDataFromEdge) return;
      fromData[k] = data[k];
    });
    if (Object.keys(fromData).length === 0) return;
    setFormValues(prev => {
      const hasChange = Object.keys(fromData).some(
        k => prev[k] !== fromData[k],
      );
      if (!hasChange) return prev;
      return { ...prev, ...fromData };
    });
  }, [data]);

  useEffect(() => {
    const fromData = (data.blobConfig as string) ?? '';
    setBlobConfig(fromData);
  }, [data.blobConfig]);

  const blobOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = blobOpen && !blobOpenRef.current;
    blobOpenRef.current = !!blobOpen;
    if (!blobOpen || blobConfig?.trim()) return;
    if (!justOpened) return;
    const merged = enrichMergedWithObjectDefaults(
      { ...data, provider, mode, ...formValues },
      provider,
      mode,
    );
    setBlobConfig(serializeNodeToBlobConfig(merged));
  }, [blobOpen, blobConfig, data, provider, mode, formValues]);

  return {
    provider,
    setProviderState,
    mode,
    setMode,
    blobConfig,
    setBlobConfig,
    blobOpen,
    setBlobOpen,
    formValues,
    setFormValues,
    isLoading,
    setIsLoading,
    isChangingDirectly,
    setIsChangingDirectly,
    whoIsChanging,
    setWhoIsChanging,
    hasToExecute,
    setHasToExecute,
    validationErrorFieldName,
    setValidationErrorFieldName,
    userEditedFieldsRef,
    dataRef,
    formValuesRef,
    config,
    isNodeConnected,
  };
}
